import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { MapPin, Phone, Loader2, AlertCircle, RefreshCw } from 'lucide-react'

// Memoized request item component
const RequestItem = React.memo(({ request, onAssign }) => (
  <div className={`p-2 rounded text-xs sm:text-sm border ${
    request.priority === 'high' ? 'bg-red-50 border-red-200' :
    request.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
    'bg-blue-50 border-blue-200'
  }`}>
    <div className="flex items-center justify-between">
      <span>{request.icon} {request.priority} - {request.description}</span>
      <Button 
        size="sm" 
        variant="outline" 
        className="text-xs"
        onClick={() => onAssign(request.id)}
      >
        Assign
      </Button>
    </div>
  </div>
))

RequestItem.displayName = 'RequestItem'

// Memoized available unit component
const AvailableUnit = React.memo(({ unit, onSelect }) => (
  <div className="flex items-center justify-between text-xs sm:text-sm">
    <span>{unit.id}</span>
    <div className="flex items-center gap-2">
      <Badge variant="default" className="text-xs">Available</Badge>
      <Button 
        size="sm" 
        variant="outline" 
        className="text-xs"
        onClick={() => onSelect(unit.id)}
      >
        Select
      </Button>
    </div>
  </div>
))

AvailableUnit.displayName = 'AvailableUnit'

const DispatchTab = () => {
  const [pendingRequests, setPendingRequests] = useState([])
  const [availableUnits, setAvailableUnits] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Memoized action handlers
  const handleAssignRequest = useCallback((requestId) => {
    // Implement assignment logic
  }, [])

  const handleSelectUnit = useCallback((unitId) => {
    // Implement unit selection logic
  }, [])

  const handleRefresh = useCallback(() => {
    // Refresh data
    window.location.reload()
  }, [])

  // Fetch dispatch data
  useEffect(() => {
    const fetchDispatchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Mock data - replace with actual API calls
        const mockRequests = [
          {
            id: 'REQ-001',
            priority: 'high',
            description: 'Cardiac Emergency',
            icon: '🚨'
          },
          {
            id: 'REQ-002',
            priority: 'medium',
            description: 'Scheduled Transport',
            icon: '⚠️'
          },
          {
            id: 'REQ-003',
            priority: 'low',
            description: 'Routine Pickup',
            icon: 'ℹ️'
          }
        ]

        const mockUnits = [
          { id: 'AMB-001' },
          { id: 'AMB-008' },
          { id: 'AMB-015' }
        ]
        
        setPendingRequests(mockRequests)
        setAvailableUnits(mockUnits)
      } catch (err) {
        setError('Failed to load dispatch data. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchDispatchData()
  }, [])

  // Loading component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin mr-2" />
      <span className="text-gray-600">Loading dispatch data...</span>
    </div>
  )

  // Error component
  const ErrorDisplay = ({ error, onRetry }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div className="flex items-center">
        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
        <div>
          <h3 className="text-sm font-medium text-red-800">Error loading dispatch data</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="!pb-0 px-4 sm:px-6 pt-4 sm:pt-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-serif text-base sm:text-lg">Dispatch Center</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Real-time dispatch operations and assignments.</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorDisplay error={error} onRetry={() => window.location.reload()} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <div className="aspect-video flex flex-col items-center justify-center bg-muted/50 rounded-lg border-2 border-dashed p-4 text-center">
                  <MapPin className="h-8 w-8 mb-2 text-gray-400" />
                  <span className="text-lg sm:text-xl">Dispatch Map</span>
                  <br />
                  <span className="text-xs sm:text-sm text-muted-foreground">Vehicle Locations & Assignments</span>
                </div>
              </div>
              <div className="space-y-4">
                <Card>
                  <CardHeader className="px-3 sm:px-4 pt-3 sm:pt-4">
                    <CardTitle className="text-sm sm:text-lg">Pending Requests</CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-4 pb-3 sm:pb-4">
                    <div className="space-y-2">
                      {pendingRequests.map((request) => (
                        <RequestItem
                          key={request.id}
                          request={request}
                          onAssign={handleAssignRequest}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="px-3 sm:px-4 pt-3 sm:pt-4">
                    <CardTitle className="text-sm sm:text-lg">Available Units</CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-4 pb-3 sm:pb-4">
                    <div className="space-y-2">
                      {availableUnits.map((unit) => (
                        <AvailableUnit
                          key={unit.id}
                          unit={unit}
                          onSelect={handleSelectUnit}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default DispatchTab
